'use client';

import { ControlledEmailInput } from '@/components/auth/controlled-email-input';
import { ControlledPasswordInput } from '@/components/auth/controlled-password-input';
import { FormSubmitButton } from '@/components/auth/form-submit-button';
import { OtpVerifyForm } from '@/components/auth/otp-verify-form';
import { authClient } from '@/lib/auth-client';
import { useLoadingCallback } from '@/lib/hooks/use-loading-callback';
import { authToasts } from '@/lib/utils/toast';
import { registerSchema } from '@/schemas/auth';
import { Form } from '@heroui/form';
import { Input } from '@heroui/input';
import { zodResolver } from '@hookform/resolvers/zod';
import { User } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { z } from 'zod';

type FormData = z.infer<typeof registerSchema>;

export function RegisterForm() {
    const router = useRouter();
    const [currentStep, setCurrentStep] = useState<'register' | 'otpVerify'>(
        'register'
    );
    const [registeredEmail, setRegisteredEmail] = useState('');

    const registerForm = useForm<FormData>({
        resolver: zodResolver(registerSchema),
        mode: 'onChange', // Real-time validation
        defaultValues: {
            name: '',
            email: '',
            password: '',
        },
    });

    const [handleRegister, loading] = useLoadingCallback(
        async (data: FormData) => {
            const response = await authClient.signUp.email(data);
            if (response.error) {
                authToasts.registrationFailed(
                    response.error.message || 'Registration failed'
                );
            } else {
                // User created, now send OTP for verification and move to OTP step
                const sendOtpResponse =
                    await authClient.emailOtp.sendVerificationOtp({
                        email: data.email,
                        type: 'email-verification',
                    });

                if (sendOtpResponse.error) {
                    authToasts.emailVerificationFailed(
                        sendOtpResponse.error.message ||
                            'Failed to send verification email.'
                    );
                } else {
                    authToasts.emailVerificationSent(); // Indicate OTP sent
                    setRegisteredEmail(data.email);
                    setCurrentStep('otpVerify');
                }
            }
        }
    );

    const handleVerificationSuccess = useCallback(
        async (otp: string) => {
            const response = await authClient.emailOtp.verifyEmail({
                email: registeredEmail,
                otp,
            });

            if (response.error) {
                authToasts.emailVerificationFailed(
                    response.error.message || 'Verification failed'
                );
            } else {
                authToasts.emailVerificationSuccess();
                router.refresh(); // Force a session refresh
                router.push('/account/settings'); // Redirect to account settings after successful verification
            }
        },
        [registeredEmail, router]
    );

    const handleResendVerification = useCallback(async () => {
        const response = await authClient.emailOtp.sendVerificationOtp({
            email: registeredEmail,
            type: 'email-verification',
        });

        if (response.error) {
            authToasts.emailVerificationFailed(
                response.error.message || 'Failed to send verification email.'
            );
        } else {
            authToasts.emailVerificationSent(); // Indicate OTP sent
        }

        return response;
    }, [registeredEmail]);

    if (currentStep === 'otpVerify') {
        return (
            <OtpVerifyForm
                email={registeredEmail}
                onResend={handleResendVerification}
                onVerify={handleVerificationSuccess}
                submitButtonText="Verify Email"
            />
        );
    }

    return (
        <Form
            className="flex flex-col gap-2"
            onSubmit={registerForm.handleSubmit(handleRegister)}
        >
            <Controller
                control={registerForm.control}
                name="name"
                render={({ field, fieldState }) => (
                    <Input
                        {...field}
                        errorMessage={fieldState.error?.message}
                        isInvalid={!!fieldState.error}
                        label="Full Name"
                        labelPlacement="outside"
                        placeholder="John Doe"
                        startContent={<User className="text-default-400" />}
                        variant="bordered"
                    />
                )}
            />
            <ControlledEmailInput control={registerForm.control} name="email" />
            <ControlledPasswordInput
                control={registerForm.control}
                name="password"
            />
            <FormSubmitButton
                color="primary"
                control={registerForm.control}
                fullWidth
                isLoading={loading}
            >
                Create An Account
            </FormSubmitButton>
        </Form>
    );
}
